import { ReactNode, useMemo } from "react";
import { useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { classNames, TranslatedString } from "@ct-react/core";
import { BasicPrice } from "@shared/graphql";
import { common } from "../../i18n/sharable-defs";
import { useTools } from "../../hooks/tools";
import { SkeletonableProps } from "../../tools/components";
import SpriteIcon from "../medias/sprite-icon";
import "./title.scss";

type Props = {
  title: TranslatedString;
  subTitle: ReactNode;
  features: Partial<{ maxGuests: number, bedrooms: number, bathrooms: number, livingSpace: number, plotSpace: number, rooms: number }>
  price?: BasicPrice;
}

const TitlePart = ({ className, loading, data }: SkeletonableProps<Props>) => {

  const intl = useIntl();
  const { format } = useTools();

  const withFeatures = useMemo(() => {
    if (!data) return false;
    return Object.entries(data.features).filter(([ k, v ]) => k !== "__typename" && typeof v !== "undefined").length > 0;
  }, [ data ]);

  const wrapperClasses = classNames("title-part", className);

  if (loading || !data)
    return (
      <div className={wrapperClasses}>
        <div className="sub-title"><Skeleton width="10%" /></div>
        <h1><Skeleton width="65%" /></h1>
        <Skeleton containerClassName="features" width="40%" />
        <Skeleton containerClassName="price" width="15%" />
      </div>);

  return (
    <div className={wrapperClasses}>

      <div className="sub-title">{data.subTitle}</div>
      <h1>{data.title.value}</h1>

      {withFeatures &&
        <ul className="features">
          {!!data.features.maxGuests &&
            <li>
              <SpriteIcon hash="features.person" />
              {intl.formatMessage(common.countedGuests, { count: data.features.maxGuests })}
            </li>
          }
          {!!data.features.bedrooms &&
            <li>
              <SpriteIcon hash="features.bedroom" />
              {intl.formatMessage(common.countedBedrooms, { count: data.features.bedrooms })}
            </li>
          }
          {!!data.features.bathrooms &&
            <li>
              <SpriteIcon hash="features.bathroom" />
              {intl.formatMessage(common.countedBathrooms, { count: data.features.bathrooms })}
            </li>
          }
          {(!!data.features.livingSpace || !!data.features.plotSpace) &&
            <li>
              <SpriteIcon hash="features.surface" />
              {`${data.features.livingSpace || data.features.plotSpace} m²`}
            </li>
          }
          {!!data.features.rooms &&
            <li>
              <SpriteIcon hash="features.room" />
              {data.features.rooms}
            </li>
          }
        </ul>
      }

      {!!data.price &&
        <span className="price">{format.anyPrice(data.price)}</span>
      }
    </div>);

};

export default TitlePart;
